/* eslint-disable sort-imports */
import { BulletinActionsMenu } from 'src/components/bulletins-admin-table';
import {
    BulletinActionDialog,
    useBulletinActions,
} from 'src/components/bulletins-admin-table/bulletin-actions';
import {
    BulletinDateField as DateField,
    BulletinTextField as Field,
} from 'src/components/bulletins-form';
import {
    UPDATE_BULLETIN_PATH,
    VIEW_BULLETIN_PATH,
} from 'src/components/bulletins-form/tabs';
import { BulletinsService } from 'src/services/bulletins.service';
import { ComboBox } from 'src/components/ui/combobox';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import {
    BULLETIN_DOCUMENT_TYPE,
    MAX_DOCUMENT_BYTES,
    uploadBulletinDocument,
} from 'src/utils/bulletin-document-upload';
import { IAdminBulletinItem } from 'src/services/models/bulletins.service';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import { RadioButton } from 'src/components/ui/radioButton';
import Spinner from 'src/components/ui/spinner';
import React, {
    ChangeEvent,
    FormEvent,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    BULLETIN_COUNTRY_NONE,
    BULLETIN_LANGUAGE_CODES,
    BULLETIN_LANGUAGES,
    BULLETIN_TYPE_CODES,
    BULLETIN_TYPES,
    formatDateForApi,
    getBulletinCountryCode,
    getBulletinLanguageName,
    getBulletinTypeName,
    getMinSelectableDate,
    toDateInputValue,
} from 'src/utils/bulletins';

import '../upload-bulletin/styles.scss';
import './styles.scss';

const BULLETIN_NUMBER_MAX_LENGTH = 60;
const BULLETIN_TITLE_MAX_LENGTH = 100;
const EMPTY_ERRORS = 0;
const SCROLL_TOP = 0;
const PDF_MIME_TYPE = 'application/pdf';
const SELECT_DISTRIBUTION_RULE_PATH =
    '/dashboard/systemadmin/view-distribution-rule';

interface IUpdateBulletinForm {
    bulletin: IAdminBulletinItem;
    file: File | null;
    bulletinNumber: string;
    bulletinTitle: string;
    type: string;
    language: string;
    startDate: string;
    endDate: string;
    distributionRule: string;
    distributionId: string;
    country: string;
}

interface IUpdateBulletinNavigationState {
    bulletin?: IAdminBulletinItem;
    formState?: IUpdateBulletinForm;
    selectedRule?: {
        distributionId: string;
        ruleName: string;
        country?: string;
    };
}

type UpdateBulletinField =
    | 'file'
    | 'bulletinNumber'
    | 'bulletinTitle'
    | 'type'
    | 'startDate'
    | 'distributionRule';
type UpdateBulletinErrors = Partial<Record<UpdateBulletinField, string>>;

const CHANGEABLE_FIELDS = [
    'bulletinNumber',
    'bulletinTitle',
    'type',
    'language',
    'startDate',
    'endDate',
    'distributionId',
    'country',
] as const;

const buildForm = (
    state: IUpdateBulletinNavigationState | null,
): IUpdateBulletinForm | null => {
    if (state?.formState) {
        const { selectedRule } = state;

        return {
            ...state.formState,
            ...(selectedRule && {
                distributionRule: selectedRule.ruleName,
                distributionId: selectedRule.distributionId,
                // The rule decides the country; a rule without country is 0.
                country: getBulletinCountryCode(selectedRule.country),
            }),
        };
    }

    const bulletin = state?.bulletin;

    if (!bulletin) {
        return null;
    }

    return {
        bulletin,
        file: null,
        bulletinNumber: String(bulletin.bulletinNumber ?? ''),
        bulletinTitle: bulletin.title ?? '',
        type: getBulletinTypeName(bulletin.bulletinType),
        language: getBulletinLanguageName(bulletin.bulletinLanguage),
        startDate: toDateInputValue(bulletin.validFrom),
        endDate: toDateInputValue(bulletin.validThrough),
        distributionRule: bulletin.distributionName ?? '',
        distributionId: bulletin.distributionId ?? '',
        country: String(bulletin.country ?? '').trim() || BULLETIN_COUNTRY_NONE,
    };
};

// The API does not return the stored file name, so it is derived from the bulletin number.
const getCurrentFileName = (bulletin: IAdminBulletinItem): string | undefined =>
    bulletin.bulletinNumber ? `${bulletin.bulletinNumber}.pdf` : undefined;

const UpdateBulletin: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [form, setForm] = useState<IUpdateBulletinForm | null>(() =>
        buildForm(location.state as IUpdateBulletinNavigationState | null),
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState(false);
    const [isSubmitMessageVisible, setIsSubmitMessageVisible] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<UpdateBulletinErrors>({});

    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.UpdateBulletinPage.${key}`, { defaultValue });
    const uploadText = (key: string, defaultValue: string) =>
        t(`SystemAdmin.UploadBulletinPage.${key}`, { defaultValue });

    const { requestExpire, requestDelete, dialogProps } = useBulletinActions({
        onSuccess: ({ message }) =>
            navigate(VIEW_BULLETIN_PATH, {
                state: { successMessage: message },
            }),
        onError: (message) => {
            setSubmitError(true);
            setSubmitMessage(message);
            setIsSubmitMessageVisible(true);
            window.scrollTo(SCROLL_TOP, SCROLL_TOP);
        },
    });

    useEffect(() => {
        if (!form) {
            navigate(VIEW_BULLETIN_PATH, { replace: true });
        }
    }, []);

    const minDate = useMemo(
        () =>
            getMinSelectableDate(
                form?.bulletin.validFrom,
                form?.bulletin.validThrough,
            ),
        [form?.bulletin],
    );

    // Values as stored, to know whether the user changed anything.
    const savedForm = useMemo(
        () => (form ? buildForm({ bulletin: form.bulletin }) : null),
        [form?.bulletin],
    );

    if (!form) {
        return null;
    }

    const updateForm = (
        field: keyof Omit<IUpdateBulletinForm, 'bulletin'>,
        value: string | File | null,
    ) => {
        setForm((currentForm) =>
            currentForm ? { ...currentForm, [field]: value } : currentForm,
        );
        setFieldErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[field as UpdateBulletinField];
            return nextErrors;
        });
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateForm('file', event.target.files?.[0] ?? null);
    };

    const isDirty =
        Boolean(form.file) ||
        CHANGEABLE_FIELDS.some((field) => form[field] !== savedForm?.[field]);
    const isFormValid = Boolean(
        form.bulletinNumber.trim() &&
            form.bulletinTitle.trim() &&
            form.type &&
            form.startDate &&
            form.distributionRule,
    );

    const validateForm = (): UpdateBulletinErrors => {
        const requiredMessage = t('Label.Field.Error.Required', {
            defaultValue: 'Field required',
        });
        const errors: UpdateBulletinErrors = {};

        if (form.file && form.file.type !== PDF_MIME_TYPE) {
            errors.file = uploadText(
                'FileError',
                'Please attach a valid PDF file.',
            );
        } else if (form.file && form.file.size > MAX_DOCUMENT_BYTES) {
            errors.file = uploadText(
                'FileSizeError',
                'File size not allowed. Maximum allowed 10MB.',
            );
        }
        if (!form.bulletinNumber.trim())
            errors.bulletinNumber = requiredMessage;
        if (!form.bulletinTitle.trim()) errors.bulletinTitle = requiredMessage;
        if (!form.type) errors.type = requiredMessage;
        if (!form.startDate) errors.startDate = requiredMessage;
        if (!form.distributionRule) errors.distributionRule = requiredMessage;

        return errors;
    };

    const handleSelectRule = () => {
        navigate(SELECT_DISTRIBUTION_RULE_PATH, {
            state: {
                selectMode: true,
                returnTo: UPDATE_BULLETIN_PATH,
                formState: form,
            },
        });
    };

    const handleBack = () => navigate(VIEW_BULLETIN_PATH);

    const handleViewDocument = async () => {
        const newTab = window.open('', '_blank');
        if (!newTab) return;

        try {
            const response = await new BulletinsService().getBulletin(
                form.bulletin.documentId,
            );

            if (response.status !== HttpStatusCode.SUCCESS) {
                throw new Error('Unable to retrieve the current bulletin PDF');
            }

            const contentType = String(
                response.headers?.['content-type'] || '',
            );
            const blob = response.data as Blob;

            if (contentType.includes('application/json')) {
                const { downloadUrl } = JSON.parse(await blob.text());
                newTab.location.assign(downloadUrl);
            } else {
                newTab.location.assign(URL.createObjectURL(blob));
            }
        } catch {
            newTab.close();
            setSubmitError(true);
            setSubmitMessage(
                text('ViewDocumentError', 'Unable to open the bulletin PDF.'),
            );
            setIsSubmitMessageVisible(true);
        }
    };

    const openDocument = (event: React.MouseEvent) => {
        event.preventDefault();
        void handleViewDocument();
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const errors = validateForm();
        setFieldErrors(errors);
        if (Object.keys(errors).length > EMPTY_ERRORS) {
            setSubmitError(true);
            setSubmitMessage(
                uploadText(
                    'ValidationError',
                    'Please correct the highlighted fields before submitting.',
                ),
            );
            setIsSubmitMessageVisible(true);
            return;
        }
        if (!isFormValid || isSubmitting) return;

        setIsSubmitting(true);
        setSubmitMessage('');
        setSubmitError(false);
        setIsSubmitMessageVisible(false);
        let isUpdated = false;

        try {
            const service = new BulletinsService();

            // The current PDF stays untouched unless a new file was selected.
            if (form.file) {
                await uploadBulletinDocument(
                    service,
                    form.file,
                    form.bulletin.documentId,
                );
            }

            await service.upsertBulletin({
                id: form.bulletin.bulletinId,
                documentId: form.bulletin.documentId,
                documentType: BULLETIN_DOCUMENT_TYPE,
                bulletinType: BULLETIN_TYPE_CODES[form.type],
                bulletinNumber: form.bulletinNumber.trim(),
                distributionId: form.distributionId,
                title: form.bulletinTitle.trim(),
                validFromDate: formatDateForApi(form.startDate),
                validThroughDate: form.endDate
                    ? formatDateForApi(form.endDate)
                    : null,
                language: BULLETIN_LANGUAGE_CODES[form.language],
                country: form.country,
                visualStyles: form.bulletin.visualStyles || '{}',
            });
            isUpdated = true;
        } catch {
            setSubmitError(true);
            setSubmitMessage(
                text(
                    'SubmitError',
                    'Unable to update bulletin. Please try again later.',
                ),
            );
            setIsSubmitMessageVisible(true);
        } finally {
            setIsSubmitting(false);
        }

        if (isUpdated) {
            navigate(VIEW_BULLETIN_PATH, {
                state: {
                    successMessage: text(
                        'SubmitSuccess',
                        'Bulletin was updated successfully.',
                    ),
                },
            });
        }
    };

    return (
        <>
            <div className='update-bulletin__back-bar'>
                <button
                    className='c-btn c-btn--link c-btn--small c-btn--simple'
                    type='button'
                    onClick={handleBack}
                >
                    <i
                        className='c-btn__icon c-icon c-icon--[semantic-back]'
                        aria-hidden='true'
                        role='img'
                    ></i>
                    <span className='c-btn__text'>{text('Back', 'Back')}</span>
                </button>
            </div>
            <main className='upload-bulletin update-bulletin'>
                {submitMessage && (
                    <Notification
                        content={submitMessage}
                        type={
                            submitError
                                ? NotificationType.ERROR
                                : NotificationType.SUCCESS
                        }
                        visibility={isSubmitMessageVisible}
                        setVisibility={setIsSubmitMessageVisible}
                    />
                )}

                <section className='c-form-section upload-bulletin__card update-bulletin__card'>
                    <div className='c-form-section__content'>
                        <div className='update-bulletin__header'>
                            <div>
                                <h1>{text('Title', 'Update Bulletin')}</h1>
                                <p className='upload-bulletin__description'>
                                    {text(
                                        'Description',
                                        'Enter information about the bulletin in the fields below.',
                                    )}
                                </p>
                            </div>
                            <BulletinActionsMenu
                                item={form.bulletin}
                                variant='circle'
                                onExpire={requestExpire}
                                onDelete={requestDelete}
                            />
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className='upload-bulletin__fields'>
                                <div className='c-form-field upload-bulletin__file-field'>
                                    <label
                                        className='c-form-field__label'
                                        htmlFor='bulletin-file'
                                    >
                                        {uploadText(
                                            'FileLabel',
                                            'Attach bulletin (PDF)',
                                        )}
                                        *
                                    </label>
                                    <div
                                        className={`c-upload ${
                                            fieldErrors.file ? 'is-error' : ''
                                        }`}
                                    >
                                        <input
                                            id='bulletin-file'
                                            className={`c-upload__input ${
                                                fieldErrors.file
                                                    ? 'is-error'
                                                    : ''
                                            }`}
                                            type='file'
                                            accept={PDF_MIME_TYPE}
                                            onChange={handleFileChange}
                                        />
                                        {fieldErrors.file && (
                                            <span className='c-error-message'>
                                                {fieldErrors.file}
                                            </span>
                                        )}
                                        <div className='c-upload__description'>
                                            <span className='c-upload__description-supplementary-text'>
                                                {form.file?.name ??
                                                    getCurrentFileName(
                                                        form.bulletin,
                                                    ) ??
                                                    text(
                                                        'CurrentFile',
                                                        'Current bulletin PDF',
                                                    )}
                                            </span>
                                        </div>
                                        <div
                                            className='c-upload__button c-btn'
                                            aria-hidden='true'
                                        >
                                            <span>
                                                {uploadText(
                                                    'ChooseFile',
                                                    'Choose File',
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <a
                                        className='update-bulletin__view-document-link'
                                        href='#'
                                        onClick={openDocument}
                                    >
                                        <i
                                            className='c-icon c-icon--[semantic-file]'
                                            aria-hidden='true'
                                        ></i>
                                        {text(
                                            'ViewCurrentDocument',
                                            'View current document',
                                        )}
                                    </a>
                                </div>

                                <div className='upload-bulletin__row'>
                                    <Field
                                        id='bulletin-number'
                                        label={uploadText(
                                            'NumberLabel',
                                            'Bulletin Number',
                                        )}
                                        placeholder={uploadText(
                                            'NumberPlaceholder',
                                            'Alphanumeric with hyphens (e.g., BL-2026-09)',
                                        )}
                                        maxLength={BULLETIN_NUMBER_MAX_LENGTH}
                                        value={form.bulletinNumber}
                                        onChange={(value) =>
                                            updateForm('bulletinNumber', value)
                                        }
                                        error={fieldErrors.bulletinNumber}
                                    />
                                    <Field
                                        id='bulletin-title'
                                        label={uploadText(
                                            'TitleLabel',
                                            'Bulletin Title',
                                        )}
                                        placeholder={uploadText(
                                            'TitlePlaceholder',
                                            'Enter complete bulletin title',
                                        )}
                                        maxLength={BULLETIN_TITLE_MAX_LENGTH}
                                        value={form.bulletinTitle}
                                        onChange={(value) =>
                                            updateForm('bulletinTitle', value)
                                        }
                                        error={fieldErrors.bulletinTitle}
                                    />
                                </div>

                                <div className='upload-bulletin__row'>
                                    <div className='c-form-field'>
                                        <label
                                            className='c-form-field__label'
                                            htmlFor='bulletin-type'
                                        >
                                            {uploadText('TypeLabel', 'Type')}*
                                        </label>
                                        <ComboBox
                                            name='bulletin-type'
                                            options={BULLETIN_TYPES}
                                            placeholder={uploadText(
                                                'TypePlaceholder',
                                                'Select type',
                                            )}
                                            selectedOption={form.type}
                                            onSelect={(value: string) =>
                                                updateForm('type', value)
                                            }
                                            hasError={Boolean(fieldErrors.type)}
                                        />
                                        {fieldErrors.type && (
                                            <span className='c-error-message'>
                                                {fieldErrors.type}
                                            </span>
                                        )}
                                    </div>
                                    <div className='c-form-field'>
                                        <label className='c-form-field__label'>
                                            {uploadText(
                                                'LanguageLabel',
                                                'Language',
                                            )}
                                        </label>
                                        <RadioButton
                                            name='bulletin-language'
                                            options={BULLETIN_LANGUAGES}
                                            labels={[
                                                uploadText(
                                                    'English',
                                                    'English',
                                                ),
                                                uploadText('French', 'French'),
                                            ]}
                                            selectedOption={form.language}
                                            onSelect={(value: string) =>
                                                updateForm('language', value)
                                            }
                                        />
                                    </div>
                                </div>

                                <div className='upload-bulletin__row'>
                                    <DateField
                                        label={`${uploadText(
                                            'StartDateLabel',
                                            'Start Date',
                                        )}*`}
                                        placeholder={uploadText(
                                            'StartDatePlaceholder',
                                            'Select start date',
                                        )}
                                        value={form.startDate}
                                        minDate={minDate}
                                        onChange={(value) =>
                                            updateForm('startDate', value)
                                        }
                                        error={fieldErrors.startDate}
                                    />
                                    <DateField
                                        label={uploadText(
                                            'EndDateLabel',
                                            'End Date',
                                        )}
                                        placeholder={uploadText(
                                            'EndDatePlaceholder',
                                            'Select end date',
                                        )}
                                        value={form.endDate}
                                        minDate={minDate}
                                        onChange={(value) =>
                                            updateForm('endDate', value)
                                        }
                                    />
                                </div>

                                <div className='upload-bulletin__distribution'>
                                    <div className='c-form-field'>
                                        <label
                                            className='c-form-field__label'
                                            htmlFor='bulletin-distribution-rule'
                                        >
                                            {uploadText(
                                                'DistributionLabel',
                                                'Choose Distribution Rule',
                                            )}
                                            *
                                        </label>
                                        <div className='upload-bulletin__distribution-control'>
                                            <div className='c-input upload-bulletin__distribution-display'>
                                                <input
                                                    id='bulletin-distribution-rule'
                                                    className={`c-input__input ${
                                                        fieldErrors.distributionRule
                                                            ? 'is-error'
                                                            : ''
                                                    }`}
                                                    type='text'
                                                    readOnly
                                                    placeholder={uploadText(
                                                        'DistributionPlaceholder',
                                                        'No distribution rule selected',
                                                    )}
                                                    value={
                                                        form.distributionRule
                                                    }
                                                />
                                            </div>
                                            <button
                                                className='c-btn c-btn--secondary'
                                                type='button'
                                                onClick={handleSelectRule}
                                            >
                                                <span className='c-btn__text'>
                                                    {uploadText(
                                                        'SelectRule',
                                                        'Select Rule',
                                                    )}
                                                </span>
                                            </button>
                                            {fieldErrors.distributionRule && (
                                                <span className='c-error-message'>
                                                    {
                                                        fieldErrors.distributionRule
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <p className='upload-bulletin__helper'>
                                {uploadText(
                                    'DistributionHelp',
                                    'Click "Select Rule" to define the bulletin\'s audience in the Dealer Extranet. After choosing a distribution rule, you will be redirected back to this screen.',
                                )}
                            </p>

                            <div className='upload-bulletin__actions'>
                                <button
                                    className='c-btn c-btn--secondary'
                                    type='button'
                                    onClick={handleBack}
                                >
                                    <span className='c-btn__text'>
                                        {uploadText('Cancel', 'Cancel')}
                                    </span>
                                </button>
                                <button
                                    className='c-btn'
                                    type='submit'
                                    disabled={
                                        !isFormValid || !isDirty || isSubmitting
                                    }
                                >
                                    <span className='c-btn__text'>
                                        {isSubmitting ? (
                                            <>
                                                <Spinner />
                                                {text(
                                                    'Submitting',
                                                    'Updating...',
                                                )}
                                            </>
                                        ) : (
                                            text('Submit', 'Update Bulletin')
                                        )}
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            </main>
            <BulletinActionDialog {...dialogProps} />
            <FooterPrivacy />
        </>
    );
};

export default UpdateBulletin;
